import { AxiosResponse } from 'axios';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import {
  Configuration,
  CreateOwnerDto,
  UpdateOwnerDto,
  OwnersApi,
  DocumentsApi,
  UploadDocumentsDtoResponse,
  DocumentMetaDataDto,
  ReadDocumentDto,
  UpsertOwnerDocumentDto,
  OwnerSearchRequestDto,
  OwnerV2Dto,
  OwnerSearchResponseV2Dto,
} from '@inosio/payments-sdk';
import {
  getFetchingOwnersData,
  setFetchingOwnersData,
  getFetchingOwnersDataFailed,
  getCreatingOwner,
  setCreatingOwner,
  getCreatingOwnerFailed,
  getUpdatingOwner,
  setUpdatingOwner,
  getUpdatingOwnerFailed,
  getFetchingOwnerDetails,
  setFetchingOwnerDetails,
  getFetchingOwnerDetailsFailed,
  setDownloading,
  getDownloadingFailed,
  getDownloading,
  getUploadOwnerDocuments,
  getUploadOwnerDocumentsFailed,
  setUploadOwnerDocuments,
  fetchOwnerDocumentURL,
  setOwnerDocumentURL,
  setFetchOwnerDocumentURLFailed,
} from './ownersSlice';
import { IpaUserExtended, selectCurrentUser } from '../../../shared/sagas/auth/authSlice';
import { Stream } from 'stream';
import { requestUploadDocuments } from '../../../shared/sagas/document/documentSaga';
import { fileExtensionRemovalRegex } from '../../../utils/regex';

const { REACT_APP_IPA_BASE_PATH } = process.env;
const apiConfig = new Configuration({ basePath: REACT_APP_IPA_BASE_PATH });

export function requestFetchingOwnersData(access_token: string, OwnerDto: OwnerSearchRequestDto) {
  apiConfig.accessToken = access_token;
  const ipaOwnersAPI = new OwnersApi(apiConfig);
  return ipaOwnersAPI.searchOwners(OwnerDto);
}

export function requesUpsertOwnersDocumentMetadata(access_token: string, ownerUUID: string, docUuid: string, upsertOwnerDocumentDto: UpsertOwnerDocumentDto) {
  apiConfig.accessToken = access_token;
  const ipaOwnersAPI = new OwnersApi(apiConfig);
  return ipaOwnersAPI.upsertOwnerDocumentMetadata(ownerUUID, docUuid, upsertOwnerDocumentDto);
}

export function requestCreatingOwner(access_token: string, OwnerDto: CreateOwnerDto) {
  apiConfig.accessToken = access_token;
  const ipaOwnersAPI = new OwnersApi(apiConfig);
  return ipaOwnersAPI.createOwner(OwnerDto);
}

export function requestUpdatingOwner(access_token: string, OwnerUUID: string, OwnerDto: UpdateOwnerDto) {
  apiConfig.accessToken = access_token;
  const ipaOwnersAPI = new OwnersApi(apiConfig);
  return ipaOwnersAPI.updateOwner(OwnerUUID, OwnerDto);
}

export function requestFetchingOwnerDetails(access_token: string, OwnerUUID: string) {
  apiConfig.accessToken = access_token;
  const ipaOwnersAPI = new OwnersApi(apiConfig);
  return ipaOwnersAPI.getOwner(OwnerUUID);
}

export function requestDownloading(access_token: string, documentUuid: string, fileType: string, viewInBrowser?: boolean) {
  apiConfig.accessToken = access_token;
  const ipaDocumentsApi = new DocumentsApi(apiConfig);
  const responseType = 'arraybuffer';
  const contentDisposition = fileType;
  const headerParams = {
    'Content-Disposition': contentDisposition,
  };

  return ipaDocumentsApi.getDocumentFile(documentUuid, undefined, viewInBrowser, { responseType, headers: headerParams });
}

export function* handleFetchingOwners(action: any) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<OwnerSearchResponseV2Dto> = yield call(requestFetchingOwnersData, currentUser.accessToken, action.payload);
    const { data } = response;

    yield put(setFetchingOwnersData(data));
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(
        getFetchingOwnersDataFailed({
          message: error?.response?.data?.message,
          statusCode: error?.response?.data?.statusCode,
        }),
      );
    } else {
      yield put(getFetchingOwnersDataFailed({ ...error }));
    }
  }
}

export function* handleCreatingOwner(action: any) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<OwnerV2Dto> = yield call(requestCreatingOwner, currentUser.accessToken, action.payload);
    const { data } = response;

    yield put(setCreatingOwner(data));
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(
        getCreatingOwnerFailed({
          message: error?.response?.data?.message,
          statusCode: error?.response?.data?.statusCode,
        }),
      );
    } else {
      yield put(getCreatingOwnerFailed({ ...error }));
    }
  }
}

export function* handleUpdatingOwner(action: { type: typeof getUpdatingOwner; payload: { ownerUuid: string; updateOwnerDto: UpdateOwnerDto } }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<OwnerV2Dto> = yield call(requestUpdatingOwner, currentUser.accessToken, action.payload.ownerUuid, action.payload.updateOwnerDto);
    const { data } = response;

    yield put(setUpdatingOwner(data));
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(
        getUpdatingOwnerFailed({
          message: error?.response?.data?.message,
          statusCode: error?.response?.data?.statusCode,
        }),
      );
    } else {
      yield put(getUpdatingOwnerFailed({ ...error }));
    }
  }
}

export function* handleFetchingOwnerDetails(action: any) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<OwnerV2Dto> = yield call(requestFetchingOwnerDetails, currentUser.accessToken, action.payload);
    const { data } = response;

    yield put(setFetchingOwnerDetails(data));
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(
        getFetchingOwnerDetailsFailed({
          message: error?.response?.data?.message,
          statusCode: error?.response?.data?.statusCode,
        }),
      );
    } else {
      yield put(getFetchingOwnerDetailsFailed({ ...error }));
    }
  }
}

export function* handleDownloading(action: any) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<Stream> = yield call(requestDownloading, currentUser.accessToken, action.payload.documentUuid, action.payload.fileType);

    // download stream
    let fileName = 'attachment'; // fallback
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = filenameRegex.exec(response.headers['content-disposition']);
    if (matches != null && matches[1]) {
      fileName = matches[1].replace(/['"]/g, '');
    }
    const url = window.URL.createObjectURL(new Blob([response.data as unknown as BlobPart], { type: response.headers['content-type'] }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${fileName}`);
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(url);

    yield put(setDownloading());
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(
        getDownloadingFailed({
          message: error?.response?.data?.message,
          statusCode: error?.response?.data?.statusCode,
        }),
      );
    } else {
      yield put(getDownloadingFailed({ ...error }));
    }
  }
}

export function* handleFetchOwnerDocumentURL(action: any) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<Stream> = yield call(requestDownloading, currentUser.accessToken, action.payload.documentUuid, action.payload.fileType, true);

    const url = URL.createObjectURL(new Blob([response.data as unknown as BlobPart], { type: response.headers['content-type'] }));
    yield put(setOwnerDocumentURL(url));
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(setFetchOwnerDocumentURLFailed({ ...error?.response?.data }));
    } else {
      yield put(setFetchOwnerDocumentURLFailed({ ...error }));
    }
  }
}

export function* handleUploadOwnerDocuments(action: any) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const filesUploadResponse: AxiosResponse<UploadDocumentsDtoResponse> = yield call(
      requestUploadDocuments,
      currentUser.accessToken,
      action.payload.belongsToEntityUuid,
      action.payload.documents,
      action.payload.documentType,
    );
    const {
      data: { files },
    } = filesUploadResponse;

    yield all(
      files.map((doc: ReadDocumentDto) => {
        return call(requesUpsertOwnersDocumentMetadata, currentUser.accessToken, doc.belongsToEntityUuid, doc.remoteUuid, {
          DocumentDescription: doc.fileName.replace(fileExtensionRemovalRegex, ''),
          DocumentMetadata: {
            PropertyUuids: action.payload.propertyUuids,
            VendorUuid: action.payload.vendorUuid,
            WorkOrderUuids: action.payload.workOrderUuids,
          } as DocumentMetaDataDto,
        } as UpsertOwnerDocumentDto);
      }),
    );

    yield put(setUploadOwnerDocuments());
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(
        getUploadOwnerDocumentsFailed({
          message: error?.response?.data?.message,
          statusCode: error?.response?.data?.statusCode,
        }),
      );
    } else {
      yield put(getUploadOwnerDocumentsFailed({ ...error }));
    }
  }
}

export function* ownersSaga() {
  yield takeLatest(getFetchingOwnersData.type, handleFetchingOwners);
  yield takeLatest(getCreatingOwner.type, handleCreatingOwner);
  yield takeLatest(getUpdatingOwner.type, handleUpdatingOwner);
  yield takeLatest(getFetchingOwnerDetails.type, handleFetchingOwnerDetails);
  yield takeLatest(getDownloading.type, handleDownloading);
  yield takeLatest(fetchOwnerDocumentURL.type, handleFetchOwnerDocumentURL);
  yield takeLatest(getUploadOwnerDocuments.type, handleUploadOwnerDocuments);
}
